import { State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, cloneElement, useCallback, useMemo, useState } from 'react';
import ScanManagerColumns from 'src/assets/static/scan_manager/scanManagerColumns';
import DocumentCountTracker from 'src/components/DocumentCountTracker';
import GridWrapper from 'src/components/GridWrapper';
import Image from 'src/components/Image';
import PreviewPDF from 'src/components/PreviewPDF';
import useAuth from 'src/hooks/useAuth';
import useScanManager from 'src/hooks/useScanManager';
import useSplitter from 'src/hooks/useSplitter';
import useWindowSize from 'src/hooks/useWindowSize';
import { validSignedUrl } from 'src/utils/strings';
import GridToolbar from './GridToolBar';

export const initialScanManagerDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'creationDate',
      dir: 'desc',
    },
  ],

  filter: {
    logic: 'and',
    filters: [
      {
        logic: 'and',
        filters: [{ field: 'receiptId', value: '', operator: 'isnull' }],
      },
    ],
  },
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
  let trProps: any = {};
  trProps = {
    ...trElement.props,
    className: `${trElement.props.className} ${props.className} ${
      props.dataItem.preview ? 'border-preview-box' : ''
    }`,
  };
  return cloneElement(trElement, { ...trProps });
};

const defaultProps = {
  tableTextId: 'Records',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  GridPagerSettings: gridPagerSettings,
  rowRender,
} as const;

interface ScanManagerSplitterProps {}

const ScanManagerSplitter: FC<ScanManagerSplitterProps> = () => {
  const {
    attachmentListData,
    count,
    setAttachmentListData,
    isInvoices,
    showPrev,
    index,
    previewDocument,
    setDataState,
    dataState,
    isAttachDocumentLoading,
    getAttachDocuments,
    setIsDecrement,
  } = useScanManager();

  const { height, width } = useWindowSize();
  const [latestClickedId, setLatestClickedId] = useState(0);
  const document = previewDocument?.signedUrl;
  const { companies } = useAuth();

  const isPDF = useMemo(
    () => previewDocument?.description?.toLowerCase().endsWith('.pdf'),
    [previewDocument?.description]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const { checked } = checkboxElement;
      setAttachmentListData((prev) => prev.map((element) => ({ ...element, selected: checked })));
    },
    [setAttachmentListData]
  );

  const onRowSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setAttachmentListData((prev) => {
        const prevItems = [...prev];
        const index = event?.startRowIndex;
        prevItems[index].selected = !prevItems[index].selected;
        return prevItems;
      });
    },
    [setAttachmentListData]
  );

  const onScanManagerDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setDataState(e.dataState);
    },
    [setDataState]
  );

  const cellClickFns = useCallback(
    () => ({ latestClickedId, setLatestClickedId, refreshList: getAttachDocuments }),
    [getAttachDocuments, latestClickedId]
  );

  const itemChange = useCallback(
    (event: GridItemChangeEvent) => {
      if (event.field === 'originalFile') {
        let { field } = event;
        event.dataItem[field] = event.value;
        event.dataItem.isInvoices = isInvoices;
        setAttachmentListData((prev) =>
          [...prev].map((row: any) => {
            if (row.attachId === event.dataItem.attachId) {
              row[field] = event.value;
            }
            return row;
          })
        );
      }
    },
    [isInvoices, setAttachmentListData]
  );

  const clearFilter = useCallback(() => {
    setDataState(initialScanManagerDataState);
  }, [setDataState]);

  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters?.length,
    [dataState.filter?.filters?.length]
  );

  const gridToolbarChildren = useMemo(
    () => <GridToolbar clearFilter={clearFilter} isClearFiltersDisabled={isClearFiltersDisabled} />,
    [clearFilter, isClearFiltersDisabled]
  );
  const scanManagerTable = useMemo(
    () => (
      <GridWrapper
        columns={
          companies.length === 1
            ? ScanManagerColumns.filter((col) => col?.field !== 'companyName')
            : ScanManagerColumns
        }
        total={count || 0}
        dataSource={attachmentListData}
        dataState={dataState}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onItemChange={itemChange}
        onSelectionChange={onRowSelectionChange}
        onDataStateChange={onScanManagerDataStateChange}
        pageable={gridPagerSettings}
        height={`${height - 90}px`}
        gridToolbarChildren={gridToolbarChildren}
        fieldModifiable
        cellClick={cellClickFns}
        loading={isAttachDocumentLoading}
        key={`${showPrev}`}
        {...defaultProps}
      />
    ),
    [
      companies.length,
      count,
      attachmentListData,
      dataState,
      onHeaderSelectionChange,
      itemChange,
      onRowSelectionChange,
      onScanManagerDataStateChange,
      height,
      gridToolbarChildren,
      cellClickFns,
      isAttachDocumentLoading,
      showPrev,
    ]
  );

  const decrementSelectedIndex = useCallback(() => {
    setAttachmentListData((prev) => {
      const previousAttachments = [...prev];
      previousAttachments[index].preview = false;
      if (index !== 0) {
        previousAttachments[index - 1].preview = true;
      } else {
        setDataState((prev) => ({ ...prev, skip: (prev.skip || 0) - (prev.take || 0) }));
        setIsDecrement(true);
      }
      return previousAttachments;
    });
  }, [index, setAttachmentListData, setDataState, setIsDecrement]);

  const incrementSelectedIndex = useCallback(() => {
    setAttachmentListData((prev) => {
      const previousAttachments = [...prev];
      previousAttachments[index].preview = false;
      if ((dataState?.take || 0) - 1 !== index) {
        previousAttachments[index + 1].preview = true;
      } else {
        setDataState((prev) => ({ ...prev, skip: (prev.skip || 0) + (prev.take || 0) }));
        setIsDecrement(false);
      }
      return previousAttachments;
    });
  }, [dataState?.take, index, setAttachmentListData, setDataState, setIsDecrement]);

  const PreviewSection = useMemo(
    () =>
      previewDocument ? (
        <div>
          <DocumentCountTracker
            disableDecrement={index === 0 && dataState.skip === 0}
            disableIncrement={(dataState.skip || 0) + index + 1 === count}
            total={count}
            currentSelected={(dataState?.skip || 0) + index + 1}
            incrementFn={incrementSelectedIndex}
            decrementFn={decrementSelectedIndex}
          />
          {!isPDF && (
            <Image
              src={`${validSignedUrl(document as string)}`}
              controls
              filename={previewDocument.originalFile}
              styles={{
                width: `${width - 20}px`,
                maxHeight: `${height - 200}px`,
              }}
            />
          )}
          {isPDF && (
            <PreviewPDF
              url={`${validSignedUrl(document as string)}`}
              height={`${height - 130}px`}
            />
          )}
        </div>
      ) : (
        <></>
      ),
    [
      previewDocument,
      index,
      dataState.skip,
      count,
      incrementSelectedIndex,
      decrementSelectedIndex,
      isPDF,
      document,
      width,
      height,
    ]
  );

  const FullPage = useSplitter(scanManagerTable, PreviewSection, showPrev);

  return FullPage;
};
export default ScanManagerSplitter;
